'use client'

import type { ExperimentClient } from '@amplitude/experiment-js-client'
import { BuilderComponent, builder } from '@builder.io/react'
import type { BuilderComponentProps } from '@builder.io/react/dist/types/src/components/builder-component.component'
import type { BuilderContent } from '@builder.io/sdk'
import type { UserAttributes } from '@builder.io/sdk/dist/src/builder.class'

import '@src/components/Builder/BuilderRegistry'
import { configPublic } from '@src/config.public'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { useExperimentContext } from '@src/utils/experimentProvider'

const DEFAULT_LOCALE = 'en-us'

export interface BuilderContentWithQuery extends BuilderContent {
  query: Array<{
    '@type': '@builder.io/core:Query'
    property: string
    value: string
    operator: 'contains' | 'is' | 'isNot' | 'startsWith' | 'endsWith'
  }>
}
interface BuilderPageProps {
  content: BuilderContentWithQuery
  locale?: string
  options?: BuilderComponentProps['options']
}

builder.init(configPublic.builder.apiKey)

function exposeExperiment(content: BuilderContentWithQuery, experiment: ExperimentClient) {
  const amplitudeUserAttribute = content?.query?.find(
    (query: any) => query.property === 'amplitudeExperiment'
  )
  const builderKey = amplitudeUserAttribute?.value.split(':')[0]
  const builderValue = amplitudeUserAttribute?.value.split(':')[1].trim()
  const amplitudeVariant = experiment.variant(builderKey as any)
  const amplitudeValue = amplitudeVariant.value
  if (!!builderValue && !!amplitudeValue && builderValue === amplitudeValue) {
    experiment.exposure(builderKey as any)
  }
}

export function RenderBuilderContent({ content, options }: BuilderPageProps) {
  // this is needed for builder to set the user attributes for the page before it renders
  // (this is cached and the data will be included on subsequent requests)
  const { edit } = getBuilderPreviewMode()

  const { experimentClient } = useExperimentContext() ?? {}
  if (experimentClient) {
    exposeExperiment(content, experimentClient)
  }

  builder.setUserAttributes(options?.userAttributes as UserAttributes)
  if (content || edit) {
    return (
      <BuilderComponent
        content={content}
        model="page"
        options={options}
        locale={options?.userAttributes?.locale as string}
      />
    )
  }

  return null
}

interface BuilderSectionProps {
  model: string
  content?: any
  locale?: string
  entry?: string
  data?: any
}

export function BuilderSection({
  model,
  content,
  locale = DEFAULT_LOCALE,
  entry,
  data,
}: BuilderSectionProps) {
  return (
    <BuilderComponent model={model} content={content} locale={locale} entry={entry} data={data} />
  )
}
